import { cConst, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Combat library (plan §4.1 expansion): weapon fire, impacts and hit
 * feedback. Every effect follows the AAA impact grammar — a bright
 * sub-100ms flash for the hit "frame", a fast shock shape that sells scale,
 * mid-speed debris/sparks for energy, and slow dissipation (smoke, embers)
 * that lingers after the eye has caught the hit.
 */
/** Muzzle flash: star-shaped core flash, hot barrel sparks, one smoke curl. Aim +Y. */
export const muzzleFlashEffect = {
    version: 1,
    meta: { name: 'Muzzle Flash', duration: 0.45, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'mz_flash',
            name: 'core flash',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.09),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.1),
                    rotation: vRange(0, 360),
                    color: cConst([1, 0.97, 0.85, 1]),
                },
            },
            // camera-facing star sprite — the procMesh 'burst' shape lies flat in XZ (ground FX only)
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [1, 0.85, 0.45, 0.9]], [1, [1, 0.6, 0.2, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'mz_glow',
            name: 'hot glow',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.14),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(0.9),
                    rotation: vConst(0),
                    color: cConst([1, 0.8, 0.4, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.55, 0.15, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'mz_sparks',
            name: 'barrel sparks',
            delay: 0,
            duration: 0.04,
            maxParticles: 24,
            emitter: {
                mode: { type: 'burst', count: 14 },
                shape: { type: 'cone', angle: 20, radius: 0.05 },
                initial: {
                    lifetime: vRange(0.12, 0.3),
                    speed: vRange(5, 10),
                    spread: vConst(8),
                    size: vRange(0.04, 0.09),
                    rotation: vConst(0),
                    color: cRange([1, 0.95, 0.7, 1], [1, 0.7, 0.3, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.25, 0.45) },
            modules: [
                { type: 'drag', enabled: true, coefficient: 6 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 0.8, 0.4, 0.9]], [1, [1, 0.5, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'mz_smoke',
            name: 'smoke curl',
            delay: 0.05,
            duration: 0.05,
            maxParticles: 6,
            emitter: {
                mode: { type: 'burst', count: 3 },
                shape: { type: 'cone', angle: 14, radius: 0.06 },
                initial: {
                    lifetime: vRange(0.3, 0.45),
                    speed: vRange(1, 1.8),
                    spread: vConst(10),
                    size: vRange(0.25, 0.45),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-120, 120),
                    color: cConst([0.75, 0.73, 0.7, 0.6]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 3 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
    ],
};
/** Big fireball explosion: flash → shockwave → fire → sparks → debris → buoyant smoke → drifting embers. */
export const explosionEffect = {
    version: 1,
    meta: { name: 'Explosion', duration: 1.7, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'ex_flash',
            name: 'detonation flash',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.13),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(3.4),
                    rotation: vConst(0),
                    color: cConst([1, 0.98, 0.9, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.75, 0.35, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'ex_ring',
            name: 'shockwave',
            delay: 0.01,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.45),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(3.6),
                    rotation: vConst(0),
                    color: cConst([1, 0.85, 0.55, 0.9]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    width: { preset: 'custom', points: [{ t: 0, v: 1, outTan: 0 }, { t: 1, v: 0.12, inTan: -1 }] },
                    widthScale: 0.5,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [1, 0.7, 0.35, 0.7]], [1, [0.8, 0.4, 0.15, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'ex_fireball',
            name: 'fireball',
            delay: 0,
            duration: 0.05,
            maxParticles: 16,
            emitter: {
                mode: { type: 'burst', count: 10 },
                shape: { type: 'sphere', radius: 0.3 },
                initial: {
                    lifetime: vRange(0.35, 0.6),
                    speed: vRange(1.2, 2.8),
                    spread: vConst(0),
                    size: vRange(0.8, 1.3),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-90, 90),
                    color: cRange([1, 0.9, 0.55, 1], [1, 0.65, 0.25, 1]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 2 },
                { type: 'gravity', enabled: true, strength: -1.5 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 0.95, 1]], [0.25, [1, 0.8, 0.35, 1]], [0.55, [0.95, 0.45, 0.12, 0.9]], [0.8, [0.45, 0.2, 0.12, 0.6]], [1, [0.2, 0.12, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'fireSheet' } },
        }),
        defaultLayer({
            id: 'ex_sparks',
            name: 'spark spray',
            delay: 0,
            duration: 0.05,
            maxParticles: 40,
            emitter: {
                mode: { type: 'burst', count: 26 },
                shape: { type: 'sphere', radius: 0.15, shell: true },
                initial: {
                    lifetime: vRange(0.35, 0.8),
                    speed: vRange(5, 11),
                    spread: vConst(0),
                    size: vRange(0.05, 0.11),
                    rotation: vConst(0),
                    color: cRange([1, 0.95, 0.75, 1], [1, 0.7, 0.3, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.2, 0.45) },
            modules: [
                { type: 'gravity', enabled: true, strength: 12 },
                { type: 'drag', enabled: true, coefficient: 1.6 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 0.75, 0.35, 0.9]], [1, [0.9, 0.4, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'ex_debris',
            name: 'debris chunks',
            delay: 0,
            duration: 0.05,
            maxParticles: 14,
            emitter: {
                mode: { type: 'burst', count: 9 },
                shape: { type: 'cone', angle: 55, radius: 0.2 },
                initial: {
                    lifetime: vRange(0.6, 1.05),
                    speed: vRange(4, 8),
                    spread: vConst(12),
                    size: vRange(0.08, 0.18),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-900, 900),
                    color: cRange([0.4, 0.32, 0.26, 1], [0.24, 0.2, 0.17, 1]),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'shard' } },
            modules: [
                { type: 'gravity', enabled: true, strength: 20 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.5 } },
        }),
        defaultLayer({
            id: 'ex_smoke',
            name: 'buoyant smoke',
            delay: 0.14,
            duration: 0.1,
            maxParticles: 12,
            emitter: {
                mode: { type: 'burst', count: 8 },
                shape: { type: 'sphere', radius: 0.45 },
                initial: {
                    lifetime: vRange(0.9, 1.4),
                    speed: vRange(0.7, 1.5),
                    spread: vConst(0),
                    size: vRange(1, 1.6),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-50, 50),
                    color: cRange([0.42, 0.4, 0.4, 0.85], [0.25, 0.24, 0.25, 0.85]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.4 },
                { type: 'gravity', enabled: true, strength: -1.4 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.18, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'ex_embers',
            name: 'drifting embers',
            delay: 0.06,
            duration: 0.08,
            maxParticles: 28,
            emitter: {
                mode: { type: 'burst', count: 18 },
                shape: { type: 'sphere', radius: 0.35, shell: true },
                initial: {
                    lifetime: vRange(0.7, 1.4),
                    speed: vRange(1.5, 3.5),
                    spread: vConst(0),
                    size: vRange(0.03, 0.08),
                    rotation: vConst(0),
                    color: cRange([1, 0.75, 0.35, 1], [1, 0.5, 0.15, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 3 },
                { type: 'drag', enabled: true, coefficient: 1.2 },
                { type: 'turbulence', enabled: true, frequency: 1.4, amplitude: 4, scrollSpeed: 0.5 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [1, 0.85, 0.5, 0.9]], [1, [0.7, 0.3, 0.05, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
    ],
};
/**
 * Energy beam blast: a white-hot core ribbon inside a flowing plasma sheath,
 * whipping out along +X with overshoot and burning away — aim the instance.
 * Beam renderer showcase (the Roblox-Beam equivalent).
 */
export const energyBeamEffect = {
    version: 1,
    meta: { name: 'Energy Beam', duration: 1.2, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'be_core',
            name: 'beam core',
            delay: 0.06,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.85),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            renderer: {
                type: 'beam',
                end: [3.4, 0, 0],
                width: { preset: 'custom', points: [{ t: 0, v: 0.55, outTan: 3 }, { t: 0.3, v: 1, inTan: 0, outTan: 0 }, { t: 1, v: 0.8, inTan: -0.3 }] },
                widthScale: 0.11,
                segments: 16,
            },
            modules: [
                // whip out with overshoot, then hold
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.75, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0.35 } },
        }),
        defaultLayer({
            id: 'be_sheath',
            name: 'plasma sheath',
            delay: 0.06,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.9),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cConst([0.45, 0.75, 1, 0.9]),
                },
            },
            renderer: {
                type: 'beam',
                end: [3.4, 0, 0],
                width: { preset: 'custom', points: [{ t: 0, v: 0.5, outTan: 2 }, { t: 0.35, v: 1, inTan: 0, outTan: 0 }, { t: 1, v: 0.7, inTan: -0.4 }] },
                widthScale: 0.38,
                segments: 16,
                tiles: 2,
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 1, 1, 0.85]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: {
                blend: 'additive',
                sprite: { type: 'builtin', id: 'noise' },
                features: { uvScroll: { x: -2.8, y: 0 } }, // plasma flows toward the target
            },
        }),
        defaultLayer({
            id: 'be_muzzle',
            name: 'muzzle flare',
            delay: 0,
            duration: 0.05,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cConst([0.85, 0.95, 1, 1]),
                },
            },
            modules: [
                {
                    // pop, then simmer while the beam holds, die with it
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0, outTan: 14 }, { t: 0.08, v: 1.2, inTan: 0, outTan: -2 }, { t: 0.25, v: 0.8, inTan: 0, outTan: 0 }, { t: 0.8, v: 0.75, inTan: 0, outTan: 0 }, { t: 1, v: 0, inTan: -5 }] },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.8, [0.8, 0.92, 1, 0.9]], [1, [0.5, 0.7, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'be_sparks',
            name: 'muzzle sparks',
            delay: 0.05,
            duration: 0.05,
            maxParticles: 18,
            emitter: {
                mode: { type: 'burst', count: 12 },
                shape: { type: 'sphere', radius: 0.1, shell: true },
                initial: {
                    lifetime: vRange(0.25, 0.5),
                    speed: vRange(1.5, 3.5),
                    spread: vConst(0),
                    size: vRange(0.04, 0.08),
                    rotation: vConst(0),
                    color: cRange([0.9, 1, 1, 1], [0.55, 0.8, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.25, 0.4) },
            modules: [
                { type: 'drag', enabled: true, coefficient: 3 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.4, 0.65, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
/** Looping charge-up: energy streams collapse inward while the core swells — classic anticipation. */
export const chargeUpEffect = {
    version: 1,
    meta: { name: 'Charge-Up', duration: 1.2, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'ch_streams',
            name: 'inflow streams',
            delay: 0,
            duration: 1.2,
            maxParticles: 64,
            emitter: {
                mode: { type: 'rate', perSecond: 38 },
                shape: { type: 'ring', radius: 1.5, direction: 'inward' },
                initial: {
                    lifetime: vRange(0.42, 0.55),
                    speed: vRange(2.6, 3.4),
                    spread: vConst(6),
                    size: vRange(0.06, 0.13),
                    rotation: vConst(0),
                    color: cRange([0.55, 0.85, 1, 1], [0.35, 0.55, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.35, 0.55) },
            modules: [
                { type: 'attractor', enabled: true, position: [0, 0, 0], strength: 7 },
                {
                    // brighten as they arrive: the energy "feeds" the core
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 0.7]], [1, [1, 1, 1, 1]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'ch_motes',
            name: 'gathering motes',
            delay: 0,
            duration: 1.2,
            maxParticles: 32,
            emitter: {
                mode: { type: 'rate', perSecond: 14 },
                shape: { type: 'sphere', radius: 1.1, shell: true },
                initial: {
                    lifetime: vRange(0.5, 0.8),
                    speed: vConst(0.2),
                    spread: vConst(0),
                    size: vRange(0.04, 0.1),
                    rotation: vConst(0),
                    color: cRange([0.8, 0.95, 1, 1], [0.5, 0.7, 1, 1]),
                },
            },
            modules: [
                { type: 'attractor', enabled: true, position: [0, 0, 0], strength: 9 },
                { type: 'vortex', enabled: true, strength: 2.5 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.3, [1, 1, 1, 1]], [1, [1, 1, 1, 0.9]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'ch_core',
            name: 'swelling core',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1.2),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.1),
                    rotation: vConst(0),
                    color: cConst([0.7, 0.9, 1, 1]),
                },
            },
            modules: [
                {
                    // grow through the cycle, dip at the loop seam so the restart pop reads as a pulse
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: {
                        preset: 'custom',
                        points: [
                            { t: 0, v: 0.25, outTan: 1.2 },
                            { t: 0.75, v: 0.95, inTan: 0.6, outTan: 0.6 },
                            { t: 0.92, v: 1.1, inTan: 0, outTan: -4 },
                            { t: 1, v: 0.25, inTan: -6 },
                        ],
                    },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [0.6, 0.85, 1, 0.55]], [0.8, [0.9, 0.97, 1, 1]], [1, [1, 1, 1, 0.4]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'ch_ground',
            name: 'contracting ground ring',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1.15),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.7),
                    rotation: vConst(0),
                    color: cConst([0.45, 0.7, 1, 0.8]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 64,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.5, outTan: 0 }, { t: 1, v: 0.15, inTan: -0.4 }] },
                    widthScale: 0.28,
                },
            },
            modules: [
                {
                    // ring closes in on the core
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 1, outTan: -0.3 }, { t: 1, v: 0.08, inTan: -1.6 }] },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
    ],
};
/** Shield block: cyan flash, camera-facing energy pulse, deflected spark fan, glints. */
export const shieldBlockEffect = {
    version: 1,
    meta: { name: 'Shield Block', duration: 0.7, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'sh_flash',
            name: 'impact flash',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.12),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.5),
                    rotation: vConst(0),
                    color: cConst([0.85, 0.95, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.4, 0.7, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'sh_pulse',
            name: 'energy pulse',
            delay: 0.015,
            duration: 0.05,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 2 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vRange(0.3, 0.42),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(1.6, 2),
                    rotation: vConst(0),
                    color: cRange([0.6, 0.9, 1, 1], [0.4, 0.65, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [1, 1, 1, 0.55]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'ring' } },
        }),
        defaultLayer({
            id: 'sh_sparks',
            name: 'deflected sparks',
            delay: 0.01,
            duration: 0.05,
            maxParticles: 28,
            emitter: {
                mode: { type: 'burst', count: 18 },
                shape: { type: 'ring', radius: 0.35, arc: 150 },
                initial: {
                    lifetime: vRange(0.25, 0.5),
                    speed: vRange(3, 6.5),
                    spread: vConst(18),
                    size: vRange(0.05, 0.1),
                    rotation: vConst(0),
                    color: cRange([0.9, 1, 1, 1], [0.5, 0.8, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.25, 0.4) },
            modules: [
                { type: 'gravity', enabled: true, strength: 9 },
                { type: 'drag', enabled: true, coefficient: 2 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.35, 0.6, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'sh_glints',
            name: 'edge glints',
            delay: 0.06,
            duration: 0.08,
            maxParticles: 10,
            emitter: {
                mode: { type: 'burst', count: 6 },
                shape: { type: 'sphere', radius: 0.75, shell: true },
                initial: {
                    lifetime: vRange(0.22, 0.4),
                    speed: vConst(0.2),
                    spread: vConst(0),
                    size: vRange(0.12, 0.26),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-180, 180),
                    color: cRange([1, 1, 1, 1], [0.7, 0.9, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
    ],
};
/** Critical hit: double star-flare pop with overshoot, gold spark fountain and a snap ring. */
export const criticalHitEffect = {
    version: 1,
    meta: { name: 'Critical Hit', duration: 0.8, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'cr_flash',
            name: 'white pop',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.12),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2),
                    rotation: vConst(0),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.9, 0.5, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'cr_star',
            name: 'star flare',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.4),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.2),
                    rotation: vConst(0),
                    angularVelocity: vConst(40),
                    color: cConst([1, 0.9, 0.45, 1]),
                },
            },
            // camera-facing star sprite; procMesh 'burst' would lie flat in XZ and vanish edge-on
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.55, [1, 0.95, 0.7, 0.9]], [1, [1, 0.7, 0.25, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'cr_cross',
            name: 'counter-rotating cross',
            delay: 0.035,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.32),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.4),
                    rotation: vConst(45),
                    angularVelocity: vConst(-60),
                    color: cConst([1, 1, 0.85, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.8, 0.35, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'cr_ring',
            name: 'snap ring',
            delay: 0.02,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.3),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.4),
                    rotation: vConst(0),
                    color: cConst([1, 0.9, 0.55, 0.9]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 64,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.7, outTan: 0 }, { t: 1, v: 0.08, inTan: -1 }] },
                    widthScale: 0.3,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.7, 0.3, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'cr_sparks',
            name: 'gold sparks',
            delay: 0.01,
            duration: 0.05,
            maxParticles: 32,
            emitter: {
                mode: { type: 'burst', count: 22 },
                shape: { type: 'sphere', radius: 0.12, shell: true },
                initial: {
                    lifetime: vRange(0.3, 0.6),
                    speed: vRange(5, 10),
                    spread: vConst(0),
                    size: vRange(0.05, 0.11),
                    rotation: vConst(0),
                    color: cRange([1, 0.98, 0.8, 1], [1, 0.8, 0.35, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.25, 0.5) },
            modules: [
                { type: 'gravity', enabled: true, strength: 8 },
                { type: 'drag', enabled: true, coefficient: 2.4 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 0.9, 0.55, 0.9]], [1, [1, 0.6, 0.15, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
